import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  ButtonBase,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { salonApi, Salon } from '../services/salonService';
import { useAuthStore } from '../store/authStore';
import { useSalonContextStore } from '../store/salonContextStore';
import { PageHeader, SectionCard, EmptyState, LoadingState, StatusPill, Crumb } from '../components/ui';
import { tokens } from '../theme';
import { countLabel, initials } from '../utils/format';

const salonInitials = (name?: string) => {
  const parts = (name || '').trim().split(/\s+/);
  return initials(parts[0], parts[1], name);
};

/** Godziny na dzis z grafiku salonu (dayOfWeek: 0 = niedziela). */
const todayHours = (salon: Salon): { label: string; open: boolean } | null => {
  const today = new Date().getDay();
  const wh = salon.workingHours?.find((h) => h.dayOfWeek === today);
  if (!wh) return null;
  return wh.isClosed ? { label: 'Dziś zamknięte', open: false } : { label: `Dziś ${wh.openTime}–${wh.closeTime}`, open: true };
};

const SalonCard: React.FC<{
  salon: Salon;
  selected?: boolean;
  onOpen: () => void;
  onSettings?: () => void;
}> = ({ salon, selected, onOpen, onSettings }) => {
  const hours = todayHours(salon);
  return (
    <Paper
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '&:hover': { borderColor: tokens.lineStrong, boxShadow: 3 },
        '&:focus-within': { borderColor: tokens.plumLight },
      }}
    >
      <ButtonBase
        onClick={onOpen}
        aria-label={`Otwórz salon ${salon.name}`}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          textAlign: 'left',
          p: { xs: 2, md: 2.5 },
          gap: 1.5,
        }}
      >
        <Stack direction="row" gap={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <Avatar
            variant="rounded"
            src={salon.logoUrl || undefined}
            sx={{ width: 48, height: 48, borderRadius: '12px', bgcolor: tokens.plumSoft, color: tokens.plum, fontSize: '1rem', fontWeight: 750 }}
          >
            {salonInitials(salon.name)}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: '1.0625rem', fontWeight: 700, lineHeight: 1.35, wordBreak: 'break-word' }}>{salon.name}</Typography>
            <Stack direction="row" alignItems="center" gap={0.5} sx={{ color: tokens.muted, mt: 0.25 }}>
              <PlaceOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography noWrap sx={{ fontSize: '0.875rem', color: tokens.muted }}>{salon.city}</Typography>
            </Stack>
          </Box>
          <ChevronRightRoundedIcon sx={{ color: tokens.subtle, flexShrink: 0 }} />
        </Stack>

        <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft }}>
          {salon.address}, {salon.postalCode} {salon.city}
        </Typography>

        {salon.description && (
          <Typography
            sx={{
              fontSize: '0.875rem',
              color: tokens.muted,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {salon.description}
          </Typography>
        )}

        {(hours || selected) && (
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            {hours && <StatusPill label={hours.label} tone={hours.open ? 'success' : 'neutral'} size="small" />}
            {selected && <StatusPill label="Wybrany w panelu" tone="plum" size="small" noDot />}
          </Stack>
        )}

        <Stack
          direction="row"
          gap={2}
          flexWrap="wrap"
          sx={{ mt: 'auto', pt: 1.5, borderTop: `1px solid ${tokens.line}`, color: tokens.muted }}
        >
          <Stack direction="row" alignItems="center" gap={0.75}>
            <ContentCutOutlinedIcon sx={{ fontSize: 17 }} />
            <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
              {countLabel(salon._count?.services ?? 0, 'usługa', 'usługi', 'usług')}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap={0.75}>
            <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
            <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft, fontVariantNumeric: 'tabular-nums' }}>
              {countLabel(salon._count?.staff ?? 0, 'osoba', 'osoby', 'osób')} w zespole
            </Typography>
          </Stack>
        </Stack>
      </ButtonBase>

      {onSettings && (
        <Box sx={{ px: { xs: 1, md: 1.5 }, py: 0.75, borderTop: `1px solid ${tokens.line}`, bgcolor: tokens.surface }}>
          <Button size="small" variant="text" startIcon={<TuneOutlinedIcon />} onClick={onSettings}>
            Ustawienia salonu
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export const SalonsPage: React.FC = () => {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const { activeSalonId, setActiveSalonId } = useSalonContextStore();
  const [salons, setSalons] = useState<Salon[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  const fetchSalons = async (searchQuery?: string) => {
    try {
      setLoading(true);
      const result = await salonApi.list({ search: searchQuery || undefined });
      setSalons(result.data);
    } catch {
      setError('Nie udało się pobrać listy salonów. Odśwież stronę za chwilę.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalons();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setQuery(search.trim());
    fetchSalons(search);
  };

  const clearSearch = () => {
    setSearch('');
    setQuery('');
    fetchSalons();
  };

  const isOwner = role === 'SALON_OWNER';
  const isAdmin = role === 'SUPER_ADMIN';
  const isClient = role === 'CLIENT';
  const canCreate = isOwner || isAdmin;

  const openSettings = (salonId: string) => {
    setActiveSalonId(salonId);
    navigate('/salon/profile');
  };

  let title = 'Salony';
  let description: React.ReactNode = 'Salony dostępne w Visitio.';
  let breadcrumbs: Crumb[] | undefined;
  if (isOwner) {
    title = 'Zarządzaj salonami';
    description = 'Twoje salony w jednym miejscu — otwórz salon, żeby zobaczyć usługi, zespół i godziny otwarcia.';
  } else if (isClient) {
    title = 'Znajdź salon';
    description = 'Wybierz salon, sprawdź ofertę i zarezerwuj wizytę online.';
  } else if (isAdmin) {
    title = 'Katalog salonów';
    description = 'Aktywne salony widoczne dla klientów na platformie.';
    breadcrumbs = [{ label: 'Administracja' }];
  }

  const renderEmpty = () => {
    if (query) {
      return (
        <EmptyState
          icon={<SearchRoundedIcon />}
          title={`Nie znaleziono salonów dla „${query}”`}
          description="Sprawdź pisownię albo wpisz krótszy fragment nazwy."
          actions={<Button variant="outlined" onClick={clearSearch}>Pokaż wszystkie salony</Button>}
        />
      );
    }
    if (canCreate) {
      return (
        <EmptyState
          icon={<StorefrontOutlinedIcon />}
          title={isOwner ? 'Nie masz jeszcze salonu' : 'Katalog salonów jest pusty'}
          description="Dodaj salon, a potem ustaw usługi, zespół i godziny otwarcia, żeby przyjmować rezerwacje."
          actions={<Button variant="outlined" startIcon={<AddIcon />} onClick={() => navigate('/salons/new')}>Dodaj salon</Button>}
        />
      );
    }
    return (
      <EmptyState
        icon={<StorefrontOutlinedIcon />}
        title="Brak salonów do wyświetlenia"
        description="Nowe salony pojawią się tutaj, gdy tylko otworzą rezerwacje online."
        actions={isClient ? <Button variant="outlined" onClick={() => navigate('/bookings/my')}>Moje wizyty</Button> : undefined}
      />
    );
  };

  return (
    <Box>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={title}
        description={description}
        actions={canCreate ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/salons/new')}>
            Dodaj salon
          </Button>
        ) : undefined}
      />

      <Stack component="form" onSubmit={handleSearch} direction="row" gap={1} sx={{ mb: 2.5, maxWidth: 640 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Szukaj salonu po nazwie…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          inputProps={{ 'aria-label': 'Szukaj salonu' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon sx={{ fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
        />
        <Button type="submit" variant="outlined" size="small" sx={{ flexShrink: 0 }}>
          Szukaj
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {query && !loading && salons.length > 0 && (
        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: '0.875rem', color: tokens.inkSoft }}>
            {countLabel(salons.length, 'wynik', 'wyniki', 'wyników')} dla „{query}”
          </Typography>
          <Button size="small" variant="text" onClick={clearSearch}>Wyczyść</Button>
        </Stack>
      )}

      {loading ? (
        <LoadingState variant="cards" count={3} />
      ) : salons.length === 0 ? (
        <SectionCard>{renderEmpty()}</SectionCard>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(3, minmax(0, 1fr))' },
          }}
        >
          {salons.map((salon) => (
            <SalonCard
              key={salon.id}
              salon={salon}
              selected={isOwner && salons.length > 1 && salon.id === activeSalonId}
              onOpen={() => navigate(`/salons/${salon.id}`)}
              onSettings={isOwner ? () => openSettings(salon.id) : undefined}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
